import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  clearUserLogged,
  getFamilyDetails as apiFamilyDetails,
  getUserLogged,
  sendRegisterForm,
  setAuthToken,
  submitLogin,
  updateUserProfile as apiUpdateUserProfile,
} from "@/utils/axios/userUtils";
import { UserType } from "@/utils/userTypes";
import { PROFILE_KEYS } from "./profileHooks";
import { FAMILY_KEYS } from "./familyHooks";
import { FamilyType } from "@/features/Login/Family/types.ts";
import { AxiosResponse } from "axios";

interface UseAuthReturn {
  user: UserType | null;
  family: FamilyType | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  loginUser: (credentials: { login: string; password: string }) => Promise<any>;
  registerUser: (userData: FormData) => Promise<any>;
  logoutUser: () => void;
  loginWithGoogle: (credential: string) => Promise<any>;
  updateUserProfile: (userId: string, data: FormData) => Promise<UserType>;
  getFamilyDetails: () => Promise<FamilyType | null>;
  refreshUser: () => void;
}

export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<UserType | null>(getUserLogged());
  const [family, setFamily] = useState<FamilyType | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const refreshUser = useCallback(() => {
    const currentUser = getUserLogged();
    setUser(currentUser);
    setIsAuthenticated(!!currentUser);
  }, []);

  // État dérivé pour l'authentification
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!user);

  // Charger l'utilisateur au démarrage
  useEffect(() => {
    refreshUser();
    // Si l'utilisateur est connecté, charger ses données de famille
    if (user) {
      getFamilyDetails();
    }
  }, []);

  // Login
  const loginUser = async (credentials: {
    login: string;
    password: string;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await submitLogin(credentials);
      if (result && (result as AxiosResponse)?.status === 200) {
        refreshUser();
        return result;
      } else {
        setError("Échec de connexion");
        return null;
      }
    } catch (err) {
      setError((err as Error).message || "Échec de connexion");
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Register
  const registerUser = async (userData: FormData) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await sendRegisterForm(userData);
      if (result && result.token) {
        setAuthToken(result.token);
        refreshUser();
        return result;
      } else {
        setError("Échec d'inscription");
        return null;
      }
    } catch (err) {
      setError((err as Error).message || "Échec d'inscription");
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const logoutUser = () => {
    clearUserLogged();
    setUser(null);
    setFamily(null);
    setIsAuthenticated(false);
    // Vider le cache QueryClient
    queryClient.clear();
  };

  // Google Login (à implémenter selon vos besoins)
  const loginWithGoogle = async (credential: string) => {
    // Implémentation à faire selon votre API
    console.log("Login avec Google à implémenter:", credential);
    return null;
  };

  // Mise à jour du profil
  const updateUserProfile = async (
    userId: string,
    data: FormData
  ): Promise<UserType> => {
    setIsLoading(true);
    try {
      const result = await apiUpdateUserProfile(userId, data);

      // Normalisation de l'avatar URI si nécessaire pour éviter les doublons
      if (result.user && result.user.avatar) {
        const avatarUri = result.user.avatar;
        // Si l'avatar contient déjà un double "images/avatar/"
        if (avatarUri.includes("images/avatar/images/avatar/")) {
          // On corrige l'URI avant de mettre à jour le cache
          result.user.avatar = avatarUri.replace(
            "images/avatar/images/avatar/",
            "images/avatar/"
          );
        }
      }

      // Mise à jour du cache TanStack Query
      queryClient.setQueryData(PROFILE_KEYS.details(userId), result.user);

      // Si l'utilisateur est le user courant, mettre à jour l'état local
      if (user && user._id === userId) {
        // Le token a déjà été mis à jour dans localStorage par apiUpdateUserProfile
        refreshUser();
      }

      return result.user;
    } catch (error) {
      setError((error as Error).message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Récupérer les détails de la famille
  const getFamilyDetails = async (): Promise<FamilyType | null> => {
    if (!isAuthenticated) return null;

    try {
      const familyData = await apiFamilyDetails();
      setFamily(familyData);

      // Mise à jour du cache
      queryClient.setQueryData(FAMILY_KEYS.details(), familyData);

      return familyData;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des détails de famille:",
        error
      );
      return null;
    }
  };

  return {
    user,
    family,
    isLoading,
    isAuthenticated,
    error,
    loginUser,
    registerUser,
    logoutUser,
    loginWithGoogle,
    updateUserProfile,
    getFamilyDetails,
    refreshUser,
  };
};
